import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Check, X, Maximize2, Minimize2, Minus } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { getInitials } from "../lib/utils";

const MatrixTable = forwardRef(function MatrixTable(
  {
    companies,
    modules,
    implementations,
    allModules,
    commitments = {},
    defaultCommitment = "Jun 2026",
    showLeader = true,
    progress = {},
  },
  ref,
) {
  const [hoverRow, setHoverRow] = useState(null);
  const [hoverCol, setHoverCol] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [cssFullscreen, setCssFullscreen] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const wrapperRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // Lock body scroll while in CSS pseudo-fullscreen + close on Esc
  useEffect(() => {
    if (!cssFullscreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setCssFullscreen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [cssFullscreen]);

  const toggleFullscreen = async () => {
    // If already in CSS fullscreen, just exit it
    if (cssFullscreen) {
      setCssFullscreen(false);
      return;
    }
    // Try native Fullscreen API first
    try {
      if (!document.fullscreenElement && wrapperRef.current?.requestFullscreen) {
        await wrapperRef.current.requestFullscreen();
        return;
      }
      if (document.fullscreenElement) {
        await document.exitFullscreen?.();
        return;
      }
    } catch (e) {
      // fall through to CSS fallback
    }
    // CSS fallback (iOS Safari, older browsers)
    setCssFullscreen(true);
  };

  useImperativeHandle(ref, () => ({ toggleFullscreen }), [cssFullscreen]);

  const inFullscreen = isFullscreen || cssFullscreen;

  // Auto-scroll horizontal en fullscreen (mobile y desktop)
  useEffect(() => {
    if (!inFullscreen || !autoScroll) return;
    const el = scrollRef.current;
    if (!el) return;
    let frame;
    const tick = () => {
      if (!el) return;
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) {
        frame = window.setTimeout(tick, 200);
        return;
      }
      if (el.scrollLeft >= max - 1) {
        frame = window.setTimeout(() => {
          if (el) el.scrollTo({ left: 0, behavior: "smooth" });
          frame = window.setTimeout(tick, 1500);
        }, 2000);
        return;
      }
      el.scrollLeft += 1;
      frame = window.setTimeout(tick, 40);
    };
    frame = window.setTimeout(tick, 800);
    return () => window.clearTimeout(frame);
  }, [inFullscreen, autoScroll, companies.length, modules.length]);

  // Reset al activar fullscreen
  useEffect(() => {
    if (inFullscreen) setAutoScroll(true);
  }, [inFullscreen]);

  if (!companies.length || !modules.length) {
    return (
      <div
        className="border border-neutral-200 p-12 text-center font-mono-data text-sm uppercase tracking-[0.2em] text-neutral-500"
        data-testid="empty-state"
      >
        Sin resultados para los filtros aplicados
      </div>
    );
  }

  // Per-row completion
  const rowStats = (cId) => {
    const row = implementations[cId] || {};
    const total = allModules.length;
    const done = allModules.filter((m) => row[m.id]).length;
    return { done, total, pct: Math.round((done / total) * 100) };
  };

  // Per-column completion across visible companies
  const colStats = (mId) => {
    const total = companies.length;
    const done = companies.filter((c) => implementations[c.id]?.[mId]).length;
    return { done, total, pct: total ? Math.round((done / total) * 100) : 0 };
  };

  return (
    <div
      ref={wrapperRef}
      className={
        inFullscreen
          ? cssFullscreen
            ? "fixed inset-0 z-[9999] flex flex-col bg-white p-4 md:p-10"
            : "w-screen h-screen flex flex-col bg-white p-6 md:p-10"
          : "hidden md:block"
      }
    >
      {inFullscreen && (() => {
        let implemented = 0;
        let total = 0;
        companies.forEach((c) => {
          allModules.forEach((m) => {
            total++;
            if (implementations[c.id]?.[m.id]) implemented++;
          });
        });
        const pct = total ? Math.round((implemented / total) * 100) : 0;
        return (
          <div
            className="mb-4 md:mb-6 pb-3 md:pb-4 border-b border-neutral-200"
            data-testid="fullscreen-brand"
          >
            <div className="flex flex-wrap items-center justify-between gap-3 md:gap-6">
              <div className="flex items-center gap-2 md:gap-3">
                <img
                  src="/logo-zae.png"
                  alt="ZAE"
                  className="w-7 h-7 md:w-8 md:h-8 object-contain"
                />
                <span className="font-display text-base md:text-lg tracking-tight">
                  Implementación<span className="text-neutral-400">/</span>ZAE
                </span>
              </div>
              <div
                className="flex items-center gap-3 md:gap-4"
                data-testid="fullscreen-coverage"
              >
                <div className="flex items-baseline gap-2 md:gap-3">
                  <span className="hidden sm:inline font-mono-data text-[11px] uppercase tracking-[0.2em] text-neutral-500">
                    Cobertura
                  </span>
                  <span className="font-display text-xl md:text-2xl tracking-tight text-emerald-600">
                    {pct}%
                  </span>
                  <span className="font-mono-data text-[10px] md:text-[11px] uppercase tracking-[0.14em] text-neutral-400">
                    {implemented}/{total}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  className="inline-flex items-center justify-center w-9 h-9 border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-950 hover:text-white hover:border-neutral-950 transition-colors shrink-0"
                  data-testid="fullscreen-exit-icon"
                  aria-label="Salir de pantalla completa"
                  title="Salir de pantalla completa"
                >
                  <Minimize2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            {inFullscreen && (
              <div className="flex justify-end mt-3">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAutoScroll((v) => !v);
                  }}
                  className={`font-mono-data text-[10px] uppercase tracking-[0.16em] border px-3 py-1.5 transition-colors ${
                    autoScroll
                      ? "border-emerald-500 text-emerald-600"
                      : "border-neutral-300 text-neutral-500"
                  }`}
                  data-testid="auto-scroll-toggle"
                  title={autoScroll ? "Auto · tap para pausar" : "Pausado · tap para reanudar"}
                >
                  {autoScroll ? "Auto" : "Pausa"}
                </button>
              </div>
            )}
          </div>
        );
      })()}
      {!inFullscreen && null}
      {/* Print-only: tables chunked by modules so all columns fit across multiple pages */}
      <div className="hidden print:block" data-testid="matrix-print-chunks">
        {(() => {
          const CHUNK = 8;
          const chunks = [];
          for (let i = 0; i < modules.length; i += CHUNK) {
            chunks.push(modules.slice(i, i + CHUNK));
          }
          return chunks.map((chunkMods, ci) => (
            <table
              key={ci}
              className="print-chunk border-collapse w-full"
              style={{ pageBreakAfter: ci < chunks.length - 1 ? "always" : "auto" }}
            >
              <thead>
                <tr>
                  <th className="border border-neutral-300 p-1 text-left bg-neutral-100">
                    Planta
                  </th>
                  {chunkMods.map((m) => (
                    <th
                      key={m.id}
                      className="border border-neutral-300 p-1 text-center bg-neutral-100"
                      style={{ width: 60, fontSize: 8 }}
                    >
                      {m.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {companies.map((c) => (
                  <tr key={c.id}>
                    <td
                      className="border border-neutral-300 p-1"
                      style={{ width: 110, fontSize: 8 }}
                    >
                      <div style={{ fontWeight: 600 }}>{c.name}</div>
                      {c.zone && (
                        <div style={{ fontSize: 6, color: "#737373" }}>
                          {c.zone}
                        </div>
                      )}
                    </td>
                    {chunkMods.map((m) => {
                      const impl = Boolean(implementations[c.id]?.[m.id]);
                      return (
                        <td
                          key={m.id}
                          className="border border-neutral-300 p-1 text-center"
                          style={{ backgroundColor: impl ? "#10b981" : "#ef4444" }}
                        >
                          <span style={{ color: "#fff", fontWeight: 700, fontSize: 9 }}>
                            {impl ? "✓" : "✗"}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          ));
        })()}
      </div>

      <div
        ref={scrollRef}
        onClick={() => inFullscreen && setAutoScroll((v) => !v)}
        className={`border border-neutral-200 bg-white relative print:hidden ${
          inFullscreen ? "flex-1 overflow-auto" : "overflow-x-auto"
        }`}
        data-testid="matrix-table-wrapper"
      >
      <table
        className="border-collapse w-full"
        style={{ minWidth: 880 }}
        data-testid="matrix-table"
      >
        <thead>
          <tr>
            <th
              className="sticky left-0 top-0 z-30 bg-neutral-50 border-b border-r border-neutral-200 p-3 text-center font-mono-data text-[11px] uppercase tracking-[0.18em] text-neutral-500 min-w-[240px] align-middle"
              style={{ height: 88 }}
              scope="col"
            >
              Planta / Módulo
            </th>
            {modules.map((m, idx) => {
              const active = hoverCol === idx;
              const stats = colStats(m.id);
              const pctColor =
                stats.pct >= 80
                  ? "text-emerald-600"
                  : stats.pct >= 50
                  ? "text-amber-500"
                  : "text-red-600";
              return (
                <th
                  key={m.id}
                  scope="col"
                  className={`sticky top-0 z-20 border-b border-r border-neutral-200 p-3 text-center font-mono-data text-[11px] uppercase tracking-[0.14em] transition-colors align-top ${
                    active ? "bg-neutral-100 text-neutral-950" : "bg-neutral-50 text-neutral-700"
                  }`}
                  style={{ minWidth: 110, height: 88 }}
                  data-testid={`col-header-${m.id}`}
                >
                  <div className="flex flex-col items-center h-full gap-1">
                    <div className={`font-display text-sm tracking-tight ${pctColor}`}>
                      {stats.pct}%
                    </div>
                    {m.category && (
                      <div className="text-[9px] leading-tight text-neutral-400 tracking-[0.14em] line-clamp-1 w-full">
                        {m.category}
                      </div>
                    )}
                    <div className="font-medium leading-tight">{m.name}</div>
                  </div>
                </th>
              );
            })}
            <th
              className="sticky top-0 z-20 bg-neutral-50 border-b border-neutral-200 p-3 text-center font-mono-data text-[11px] uppercase tracking-[0.14em] text-neutral-500 min-w-[110px] align-middle"
              style={{ height: 88 }}
              scope="col"
            >
              Cobertura
            </th>
          </tr>
        </thead>
        <tbody>
          {companies.map((c, rowIdx) => {
            const stats = rowStats(c.id);
            const rowActive = hoverRow === rowIdx;
            return (
              <tr
                key={c.id}
                onMouseEnter={() => setHoverRow(rowIdx)}
                onMouseLeave={() => setHoverRow(null)}
                data-testid={`row-${c.id}`}
              >
                <th
                  scope="row"
                  className={`sticky left-0 z-10 border-b border-r border-neutral-200 p-4 text-left transition-colors ${
                    rowActive ? "bg-neutral-100" : "bg-white"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="hidden md:grid w-9 h-9 bg-neutral-950 text-white place-items-center font-mono-data text-[11px] tracking-wider shrink-0">
                      {getInitials(c.name)}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-neutral-950 truncate">
                        {c.name}{" "}
                        <span
                          className={`font-mono-data text-[11px] ${
                            stats.pct >= 80
                              ? "text-emerald-600"
                              : stats.pct >= 50
                              ? "text-amber-500"
                              : "text-red-600"
                          }`}
                        >
                          [{stats.pct}%]
                        </span>
                      </div>
                      {c.zone && (
                        <div className="font-mono-data text-[10px] uppercase tracking-[0.16em] text-neutral-500 truncate">
                          {c.zone}
                        </div>
                      )}
                      {c.leader && showLeader && (
                        <div className="text-[11px] text-neutral-500 truncate" title={c.leader}>
                          <span className="text-neutral-400">Líder: </span>
                          {c.leader}
                        </div>
                      )}
                    </div>
                  </div>
                </th>
                {modules.map((m, colIdx) => {
                  const implemented = Boolean(implementations[c.id]?.[m.id]);
                  const colActive = hoverCol === colIdx;
                  const cellProgress = progress[c.id]?.[m.id];
                  const pct =
                    cellProgress && cellProgress.expected > 0
                      ? Math.round((cellProgress.achieved / cellProgress.expected) * 100)
                      : null;
                  // Determinar tier: verde ≥85, amarillo 60-84, rojo <60 o sin datos
                  let tier = implemented ? "green" : "red";
                  if (cellProgress) {
                    if (cellProgress.expected === 0) tier = "red";
                    else if (pct >= 85) tier = "green";
                    else if (pct >= 60) tier = "amber";
                    else tier = "red";
                  }
                  const tierClass =
                    tier === "green"
                      ? "bg-emerald-500 rounded-full"
                      : tier === "amber"
                      ? "bg-amber-500"
                      : "bg-red-500";
                  const tierLabel =
                    tier === "green"
                      ? "Implementado"
                      : tier === "amber"
                      ? "En progreso"
                      : "Pendiente";
                  const TierIcon = tier === "green" ? Check : tier === "amber" ? Minus : X;
                  return (
                    <td
                      key={m.id}
                      onMouseEnter={() => setHoverCol(colIdx)}
                      onMouseLeave={() => setHoverCol(null)}
                      className={`border-b border-r border-neutral-200 p-2 text-center align-middle transition-colors ${
                        rowActive || colActive ? "bg-neutral-50" : "bg-white"
                      }`}
                      data-testid={`cell-${c.id}-${m.id}`}
                      data-status={tier}
                    >
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="inline-flex flex-col items-center justify-center gap-1 cursor-default">
                            <span
                              className={`w-5 h-5 grid place-items-center transition-transform hover:scale-110 ${tierClass}`}
                              aria-label={tierLabel}
                            >
                              <TierIcon className="w-3 h-3 text-white" strokeWidth={3.5} />
                            </span>
                            {cellProgress && (
                              <span className="font-mono-data text-[9px] text-neutral-600 leading-none whitespace-nowrap">
                                {cellProgress.achieved}/{cellProgress.expected}
                              </span>
                            )}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          className="rounded-none bg-neutral-950 text-white border border-neutral-950 font-mono-data text-[11px] uppercase tracking-[0.14em]"
                        >
                          <div>
                            {c.name} · {m.name} · {tierLabel}
                          </div>
                          {cellProgress && cellProgress.expected > 0 && (
                            <div className="mt-1 text-neutral-300 normal-case tracking-[0.12em]">
                              Avance: {cellProgress.achieved} de {cellProgress.expected} ({pct}%)
                            </div>
                          )}
                          {tier !== "green" && (
                            <div className="mt-1 text-emerald-300 normal-case tracking-[0.12em]">
                              Fecha compromiso: {commitments[c.id]?.[m.id] || defaultCommitment}
                            </div>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    </td>
                  );
                })}
                <td
                  className={`border-b border-neutral-200 p-3 text-center align-middle transition-colors ${
                    rowActive ? "bg-neutral-50" : "bg-white"
                  }`}
                  data-testid={`row-coverage-${c.id}`}
                >
                  <div className="flex flex-col items-center gap-1">
                    <div className="font-display text-sm tracking-tight">
                      {stats.pct}%
                    </div>
                    <div className="w-16 h-[3px] bg-neutral-200 relative">
                      <div
                        className="absolute inset-y-0 left-0 bg-neutral-950"
                        style={{ width: `${stats.pct}%` }}
                      />
                    </div>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
});

export default MatrixTable;
